// Labels op projectniveau (SPEC 27okt 6, in de spec "tags"; in de UI "labels" omdat taggen nu
// een collega taggen betekent): chips, toevoegen met autocomplete op bestaande tags (geen
// wildgroei) en verwijderen. Automatische tags (prijsafwijking, wacht op oplevering) verdwijnen
// vanzelf als de oorzaak weg is en kunnen niet manueel weggehaald worden.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type TagRij } from '../lib/api';
import { useToast } from './Toast';

export function TagChips({ tags, onRemove, klein = false }: { tags: TagRij[]; onRemove?: (t: TagRij) => void; klein?: boolean }) {
  if (!tags.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {tags.map((t) => (
        <span
          key={t.id}
          className={`inline-flex items-center gap-1 rounded-full border ${t.bron === 'automatisch' ? 'border-accent/40 bg-accent-50 text-accent-700' : 'border-border bg-white text-primary'} ${klein ? 'px-2 py-0 text-[10px]' : 'px-2.5 py-0.5 text-xs'} font-bold`}
          title={t.bron === 'automatisch' ? 'Automatisch label: verdwijnt vanzelf als de oorzaak weg is' : undefined}
        >
          {t.naam}
          {onRemove && t.bron === 'manueel' && (
            <button type="button" className="text-secondary hover:text-danger" onClick={() => onRemove(t)} aria-label={`Label ${t.naam} verwijderen`}>×</button>
          )}
        </span>
      ))}
    </span>
  );
}

/** Tags bekijken en beheren op één project (sleutel = P-nummer of calc:<id>). */
export function ProjectTags({ sleutel, tags, openSignaal }: {
  sleutel: string;
  tags: TagRij[];
  /** Verandert dit getal, dan opent het invoerveld (knop "Label" bovenaan de calculatie). */
  openSignaal?: number;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [invoer, setInvoer] = useState('');
  const [open, setOpen] = useState(false);
  useEffect(() => { if (openSignaal) setOpen(true); }, [openSignaal]);
  const lijstQ = useQuery({ queryKey: ['tags'], queryFn: () => api.dossier.tags(), enabled: open });
  const suggesties = useMemo(() => {
    const term = invoer.trim().toLowerCase();
    const al = new Set(tags.map((t) => t.id));
    return (lijstQ.data?.tags ?? [])
      .filter((t) => !t.automatisch && !al.has(t.id) && (!term || t.naam.toLowerCase().includes(term)))
      .slice(0, 8);
  }, [invoer, lijstQ.data, tags]);
  const verversen = () => {
    qc.invalidateQueries({ queryKey: ['project'] });
    qc.invalidateQueries({ queryKey: ['projectniveau'] });
    qc.invalidateQueries({ queryKey: ['projects'] });
    qc.invalidateQueries({ queryKey: ['tags'] });
    qc.invalidateQueries({ queryKey: ['projectlog'] });
  };
  const voegToe = useMutation({
    mutationFn: (body: { naam?: string; tagId?: string }) => api.dossier.voegTagToe(sleutel, body),
    onSuccess: () => { setInvoer(''); setOpen(false); verversen(); },
    onError: () => toast.error('Label toevoegen mislukt'),
  });
  const verwijder = useMutation({
    mutationFn: (tagId: string) => api.dossier.verwijderTag(sleutel, tagId),
    onSuccess: verversen,
    onError: () => toast.error('Label verwijderen mislukt'),
  });
  const bestaatExact = (lijstQ.data?.tags ?? []).some((t) => t.naam.toLowerCase() === invoer.trim().toLowerCase());

  return (
    <div className="flex flex-wrap items-center gap-2 relative">
      <TagChips tags={tags} onRemove={(t) => verwijder.mutate(t.id)} />
      {!open ? (
        <button type="button" className="text-xs font-bold text-accent-700 hover:underline" onClick={() => setOpen(true)}>+ Label</button>
      ) : (
        <div className="relative">
          <input
            autoFocus
            className="input py-1.5 text-sm w-48"
            placeholder="Label zoeken of nieuw…"
            value={invoer}
            maxLength={40}
            onChange={(e) => setInvoer(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { setOpen(false); setInvoer(''); }
              if (e.key === 'Enter' && invoer.trim().length >= 2) {
                e.preventDefault();
                const exact = (lijstQ.data?.tags ?? []).find((t) => t.naam.toLowerCase() === invoer.trim().toLowerCase());
                voegToe.mutate(exact ? { tagId: exact.id } : { naam: invoer });
              }
            }}
            aria-label="Label toevoegen"
          />
          <div className="absolute z-20 mt-1 w-64 rounded-md border border-border bg-white shadow-lg">
            {suggesties.map((t) => (
              <button key={t.id} type="button" className="block w-full text-left px-3 py-1.5 text-sm hover:bg-lightgrey" onClick={() => voegToe.mutate({ tagId: t.id })}>
                {t.naam} {t.vast && <span className="text-[10px] text-secondary">vast</span>}
              </button>
            ))}
            {invoer.trim().length >= 2 && !bestaatExact && (
              <button type="button" className="block w-full text-left px-3 py-1.5 text-sm hover:bg-lightgrey border-t border-border" onClick={() => voegToe.mutate({ naam: invoer })}>
                Nieuw label "<strong>{invoer.trim()}</strong>"
              </button>
            )}
            {!suggesties.length && invoer.trim().length < 2 && <p className="px-3 py-2 text-xs text-secondary">Typ om te zoeken</p>}
            <button type="button" className="block w-full text-right px-3 py-1 text-xs text-secondary border-t border-border" onClick={() => { setOpen(false); setInvoer(''); }}>Sluiten</button>
          </div>
        </div>
      )}
    </div>
  );
}
